// CS 1.6 AK-47 firing model — recoil (punchangle), accuracy and spread.
// Values verified against ReGameDLL_CS: dlls/wpn_shared/wpn_ak47.cpp
// (PrimaryAttack / AK47Fire) and dlls/weapons.cpp (KickBack, the shots-fired
// decay in ItemPostFrame). The engine fires bullets along
// `v_angle + punchangle`, so the recoil punch also steers the shots — exactly
// what this model reproduces.

export interface KickParams {
  upBase: number
  lateralBase: number
  upMod: number
  lateralMod: number
  upMax: number
  lateralMax: number
  dirChange: number
}

/** KickBack(...) arguments per player state, straight from AK47Fire(). */
const AK47_KICK: Record<'moving' | 'air' | 'duck' | 'stand', KickParams> = {
  moving: { upBase: 1.5, lateralBase: 0.45, upMod: 0.225, lateralMod: 0.05, upMax: 6.5, lateralMax: 2.5, dirChange: 7 }, // prettier-ignore
  air: { upBase: 2.0, lateralBase: 1.0, upMod: 0.5, lateralMod: 0.35, upMax: 9.0, lateralMax: 6.0, dirChange: 5 }, // prettier-ignore
  duck: { upBase: 0.9, lateralBase: 0.35, upMod: 0.15, lateralMod: 0.025, upMax: 5.5, lateralMax: 1.5, dirChange: 9 }, // prettier-ignore
  stand: { upBase: 1.0, lateralBase: 0.375, upMod: 0.175, lateralMod: 0.0375, upMax: 5.75, lateralMax: 1.75, dirChange: 8 }, // prettier-ignore
}

const BASE_ACCURACY = 0.2
const MAX_ACCURACY = 1.25
const ACCURACY_DIVISOR = 200
const MAX_SHOTS = 15
const SHOT_DECAY = 0.0225 // seconds per shot decremented while not firing
const SHOT_DECAY_DELAY = 0.4 // grace period before the shot counter decays

export interface FireState {
  onGround: boolean
  ducking: boolean
  speed: number // horizontal speed, u/s
}

/** AK47::PrimaryAttack spread cone for the current state and accuracy. */
function ak47Spread(accuracy: number, s: FireState): number {
  if (!s.onGround) return 0.04 + 0.4 * accuracy
  if (s.speed > 140) return 0.04 + 0.07 * accuracy
  return 0.0275 * accuracy // standing or ducking
}

/** KickBack params for the current state (AK47Fire branch order). */
function ak47Kick(s: FireState): KickParams {
  if (s.speed > 0) return AK47_KICK.moving
  if (!s.onGround) return AK47_KICK.air
  if (s.ducking) return AK47_KICK.duck
  return AK47_KICK.stand
}

/**
 * Stateful AK-47 fire model. `pitch`/`yaw` are the punchangle in **degrees**
 * (pitch > 0 = view kicked up); add them to the look angles each frame. Call
 * `fire()` once per round (it returns the spread cone), and `update()` every
 * frame to decay the punch and the shot counter.
 */
export class AkFireModel {
  public pitch = 0 // punchangle.x (degrees, up-positive)
  public yaw = 0 // punchangle.y (degrees)
  private shots = 0
  private accuracy = BASE_ACCURACY
  private direction = 0
  private decreaseAt = 0

  /** Reset on deploy / reload (CS resets accuracy + shots there). */
  public reset(): void {
    this.shots = 0
    this.accuracy = BASE_ACCURACY
  }

  /** Register a shot: returns the spread cone and accumulates the recoil punch. */
  public fire(state: FireState, now: number, rng: () => number = Math.random): number {
    // Spread uses the accuracy as it stands BEFORE this shot (matches source).
    const spread = ak47Spread(this.accuracy, state)

    this.shots = Math.min(this.shots + 1, MAX_SHOTS)
    this.accuracy = Math.min(
      MAX_ACCURACY,
      (this.shots * this.shots * this.shots) / ACCURACY_DIVISOR + 0.35,
    )

    const k = ak47Kick(state)
    const up = this.shots === 1 ? k.upBase : this.shots * k.upMod + k.upBase
    const lat =
      this.shots === 1 ? k.lateralBase : this.shots * k.lateralMod + k.lateralBase
    this.pitch = Math.min(this.pitch + up, k.upMax) // view kicks up, clamped
    if (this.direction === 1) {
      this.yaw = Math.min(this.yaw + lat, k.lateralMax)
    } else {
      this.yaw = Math.max(this.yaw - lat, -k.lateralMax)
    }
    if (Math.floor(rng() * (k.dirChange + 1)) === 0) {
      this.direction = 1 - this.direction
    }
    this.decreaseAt = now + SHOT_DECAY_DELAY
    return spread
  }

  /** Per-frame: decay the punch toward zero, and the shot counter when idle. */
  public update(dt: number, now: number, firing: boolean): void {
    // punchangle decay (HL V_DropPunchAngle): len -= (10 + len*0.5) * dt.
    const len = Math.hypot(this.pitch, this.yaw)
    if (len > 1e-4) {
      const next = Math.max(0, len - (10 + len * 0.5) * dt)
      const scale = next / len
      this.pitch *= scale
      this.yaw *= scale
    }
    if (!firing && this.shots > 0 && now >= this.decreaseAt) {
      this.decreaseAt = now + SHOT_DECAY
      this.shots--
      if (this.shots === 0) this.accuracy = BASE_ACCURACY
    }
  }
}
